import { App } from "antd";
import dayjs from "dayjs";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
    getApiError,
    getKpiGetPointProgressThunk,
    getPlanGetByPointIdThunk,
    getPriceListByPointThunk,
} from "tm-shared/api";
import { getStorageInventoryGetThunk } from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import {
    PaymentForm,
    Point,
    Product,
    TaskRoute,
    TaskSaleAdditional,
} from "tm-shared/types";

import PlanIcon from "@/assets/icons/plan-icon.svg?react";
import { getPointStorageId } from "@/domains/point";
import { useCatalog } from "@/domains/product";
import { SVScanButton } from "@/features/SupervisorScan";
import { useQueryFilter, useScrollToTop } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
    usePointFactSelector,
    usePointPlanSelector,
    usePointPriceListSelector,
    useStorageInventorySelector,
} from "@/hooks/redux";
import { useTaskPayloadSelector } from "@/hooks/redux/useProgressSelectors";
import { useOfflineFilteredProducts } from "@/pages/WorkPlan/Tasks/components/OfflineFilterDrawer";
import { ProductsFormCatalog } from "@/pages/WorkPlan/Tasks/SalePage/components/ProductsFormCatalog";
import { Header } from "@/shared/layout";
import { HeaderFilterButton } from "@/shared/navigation/HeaderFilterButton";
import { Meta } from "@/shared/seo";
import { setSalePaymentForm } from "@/store/slices/progress";
import { setSaleQuery } from "@/store/slices/ui";

import { OfflineFilterDrawer } from "../components/OfflineFilterDrawer";
import {
    StyledFormWarning,
    StyledSum,
    StyledTypographyFormWarning,
    StyledTypographySum,
} from "../shared";
import { SaleFiltersPanel } from "./components/SaleFiltersPanel";
import {
    MainWrapper,
    StyledPlanButton,
    StyledPromoLink,
} from "./SalePage.styled";
import {
    calculateOrderSum,
    checkIsStatisticNotBeforeMotivation,
    countActiveFilters,
    makeAdvancedProducts,
} from "./utils";
import { checkIsMML } from "./utils/checkIsMML";
import { makePlans } from "./utils/makePlans";

interface SalePageProps {
    point: Point;
    task: TaskSaleAdditional;
}

export const SalePage: FC<SalePageProps> = ({ point, task }) => {
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();
    const taskId = task.id;
    const pointId = point.id;

    // --- UI state (Redux) ---
    const { selectedFilters, hideZeroProducts, showOnlyMML } = useAppSelector(
        (s) => s.ui.productsFilter,
    );
    const searchQuery = useAppSelector(
        (state) => state.ui.saleAdditional.query,
    );

    // --- Point & offline context ---
    const offlineMode = useOfflineModeSelector();
    const storageId = getPointStorageId(point) ?? "";
    const currentPointInventory = useStorageInventorySelector(storageId) ?? {};

    // --- Plans & progress (KPI) ---
    const storePlan = usePointPlanSelector(pointId);
    const storeUnassignedPlan = usePointPlanSelector("unassignedPlan");
    const pointProgress = usePointFactSelector(pointId);
    const motivation = useAppSelector((state) => state.work.motivation);
    const motivationInfoDate = useAppSelector(
        (state) => state.offline.motivationInfoDate,
    );

    // --- Pricing ---
    const priceList = usePointPriceListSelector(pointId);

    // --- Task payload (this task's draft: products, payment form, etc.) ---
    const currentPayload = useTaskPayloadSelector(
        TaskRoute.SaleAdditional,
        taskId,
    );

    // --- Catalog ---
    const { products } = useCatalog();

    const scrollToTop = useScrollToTop("#catalog-container");

    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [isPaymentFormTouched, setIsPaymentFormTouched] = useState(false);
    const [isOnlyPlanMode, setIsOnlyPlanMode] = useState(false);
    const [isPriceListLoading, setIsPriceListLoading] = useState(false);

    const currentProducts = useMemo(
        () => currentPayload?.products ?? {},
        [currentPayload?.products],
    );
    const currentPaymentForm = currentPayload?.paymentForm;
    const currentIgnoreRemainings = currentPayload?.ignoreRemainings ?? [];

    const currentPointPlan = useMemo(
        () => makePlans(storePlan ?? {}),
        [storePlan],
    );
    const currentRoutePlan = useMemo(
        () => makePlans(storeUnassignedPlan ?? {}),
        [storeUnassignedPlan],
    );

    const mmlList = useMemo(() => {
        const isStatisticValid = checkIsStatisticNotBeforeMotivation(
            motivation?.targetDate,
            motivationInfoDate,
        );

        return Object.keys(products).reduce<Record<string, boolean>>(
            (acc, barcode) => {
                acc[barcode] = checkIsMML(
                    currentPointPlan,
                    barcode,
                    isStatisticValid,
                    pointProgress,
                );
                return acc;
            },
            {},
        );
    }, [
        motivation?.targetDate,
        motivationInfoDate,
        pointProgress,
        products,
        currentPointPlan,
    ]);

    const { sortedProducts } = useOfflineFilteredProducts({
        products,
        inventoryList: currentPointInventory,
        mmlList,
    });

    const searchFilteredProducts = useQueryFilter<Product>(
        sortedProducts,
        searchQuery,
        [
            (p) => p?.name,
            (p) => p?.barcode,
            (p) => p?.params?.description?.value,
            (p) => p?.params?.capacity?.value,
        ],
    );

    const productsWithMotivation = useMemo(() => {
        const specialMotivationGroups = Object.values(
            motivation?.motivationGroups || [],
        )
            .map((motivationGroup) => motivationGroup.group)
            .filter(({ groupType }) => groupType === "special");

        return makeAdvancedProducts(
            searchFilteredProducts,
            specialMotivationGroups,
            currentPointPlan,
            currentRoutePlan,
            pointProgress ?? {},
            priceList ?? {},
            mmlList,
        );
    }, [
        motivation?.motivationGroups,
        searchFilteredProducts,
        currentPointPlan,
        currentRoutePlan,
        pointProgress,
        priceList,
        mmlList,
    ]);

    const totalOrderSum = useMemo(
        () => calculateOrderSum(currentProducts),
        [currentProducts],
    );

    const activeFiltersCount = useMemo(
        () => countActiveFilters(selectedFilters),
        [selectedFilters],
    );

    const pageTitle = `Продаж ${point.market.contragent.name ?? ""}`;

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setSaleQuery(e.target.value));
    };

    const togglePlanFilter = useCallback(() => {
        setIsOnlyPlanMode((prev) => !prev);
        scrollToTop();
    }, [scrollToTop]);

    const handlePaymentFormChange = useCallback(
        (paymentForm: PaymentForm) => {
            if (!isPaymentFormTouched && currentPaymentForm) {
                setIsPaymentFormTouched(true);
            }

            dispatch(
                setSalePaymentForm({
                    taskId,
                    paymentForm,
                }),
            );
        },
        [isPaymentFormTouched, currentPaymentForm, dispatch, taskId],
    );

    useEffect(() => {
        if (!offlineMode && storageId) {
            dispatch(
                getStorageInventoryGetThunk({
                    storageId,
                }),
            );
            dispatch(
                getPlanGetByPointIdThunk({
                    pointId,
                    targetDate: dayjs().format(backendDateFormat.date),
                }),
            );
            dispatch(
                getKpiGetPointProgressThunk({
                    pointId,
                    targetDate: dayjs().format(backendDateFormat.date),
                }),
            );
        }
    }, [dispatch, pointId, storageId, offlineMode]);

    useEffect(() => {
        const getPriceList = async () => {
            try {
                setIsPriceListLoading(true);

                await dispatch(getPriceListByPointThunk({ pointId })).unwrap();
            } catch (error) {
                const { displayMessage } = getApiError(error);

                notification.error({
                    title: "Не вдалося завантажити ціни",
                    description: displayMessage,
                });
            } finally {
                setIsPriceListLoading(false);
            }
        };

        if (offlineMode || priceList) {
            return;
        }

        getPriceList();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, pointId]);

    const headerRightActions = useMemo(
        () => (
            <>
                <SVScanButton taskId={taskId} pointId={pointId} />

                <StyledPromoLink to={"promo"}>%</StyledPromoLink>

                <StyledPlanButton
                    type="text"
                    icon={<PlanIcon />}
                    onClick={togglePlanFilter}
                    $isPlanActive={isOnlyPlanMode}
                />
                <HeaderFilterButton
                    onClick={() => setIsFilterDrawerOpen(true)}
                    filterCount={activeFiltersCount}
                    active={hideZeroProducts || showOnlyMML}
                />
            </>
        ),
        [
            taskId,
            pointId,
            togglePlanFilter,
            isOnlyPlanMode,
            activeFiltersCount,
            hideZeroProducts,
            showOnlyMML,
        ],
    );

    return (
        <MainWrapper>
            <Meta title={pageTitle} />
            <Header title={pageTitle} back rightActions={headerRightActions} />

            {totalOrderSum > 0 && (
                <StyledSum>
                    <StyledTypographySum>
                        Сума: <strong>{totalOrderSum.toFixed(2)} грн.</strong>
                    </StyledTypographySum>
                </StyledSum>
            )}
            {isPaymentFormTouched && (
                <StyledFormWarning>
                    <StyledTypographyFormWarning>
                        Увага! Ви змінили форму оплати, продаж буде тільки по
                        ній
                    </StyledTypographyFormWarning>
                </StyledFormWarning>
            )}
            <SaleFiltersPanel
                query={searchQuery}
                onSearch={handleSearchChange}
                selectedForm={currentPaymentForm ?? null}
                onFormChange={handlePaymentFormChange}
                products={products}
            />

            <ProductsFormCatalog
                pointId={pointId}
                task={task}
                products={productsWithMotivation}
                isOnlyPlan={isOnlyPlanMode}
                loading={isPriceListLoading}
                payloadProducts={currentProducts}
                paymentForm={currentPaymentForm}
                ignoreRemainings={currentIgnoreRemainings}
                inventory={currentPointInventory}
            />

            <OfflineFilterDrawer
                open={isFilterDrawerOpen}
                setOpen={setIsFilterDrawerOpen}
                products={products}
                mmlList={mmlList}
                inventoryList={currentPointInventory}
            />
        </MainWrapper>
    );
};
